import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import { usePermission } from "../../../shared";
import { TableStateContext } from "../../../store";
import { convertDate, errorMessage, successMessage } from "../../../utils";
import {
  createFtiApi,
  getFtiDaysSettingApi,
  getFullTaxInvoicesApi,
  updateFtiDaysSettingApi,
} from "../apis";

const tableName = "full_tax_invoice";

const defaultPagination = {
  pageIndex: 1,
  pageSize: 50,
  pageCount: 0,
  total: 0,
  hasMorePages: true,
  lastPage: 1,
};

const defaultFilterValues = {
  status: null,
  stores: [],
  from_date: null,
  to_date: null,
};

// Shared by the listing and settings tabs, so the FTI settings are loaded once
// and the saved request period is ready for the create flow's order selection
export const useFullTaxInvoice = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const isCreateVisible = usePermission("fti-create");
  const isEditable = usePermission("fti-modify");
  const { filterState, filterStateDispatch } =
    React.useContext(TableStateContext);
  const [, setForceUpdate] = React.useState(0);

  const storedFilter = filterState.params[tableName];
  const storedFilterValues = {
    ...defaultFilterValues,
    ...storedFilter?.appliedFilter,
  };

  const [state, setState] = useImmer({
    invoices: [],
    pagination: storedFilter?.pagination ?? defaultPagination,
    isLoading: false,
    search: storedFilter?.search ?? "",
    filter: {
      // `values` holds the unsaved selection, `appliedValues` is what the list is filtered by
      values: storedFilterValues,
      appliedValues: storedFilterValues,
      isFilterApplied: true,
    },
    // `step` decides which create dialog is open ("orders" | "form" | null)
    createFlow: { step: null, order: null },
    settings: {
      formData: { request_period_days: "" },
      // The saved value, unsaved edits in the settings form don't affect the create flow
      requestPeriodDays: null,
      isLoading: true,
      isSubmitting: false,
      hasChanges: false,
    },
  });

  const settingsValidator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  // ================================================================ API SECTION ======================================================
  const getInvoices = async (currentState) => {
    triggerTableLoading(true);
    try {
      const response = await getFullTaxInvoicesApi(
        getRequestParams(currentState)
      );
      const { success, message, data } = response;
      if (success) {
        filterStateDispatch({
          type: "SET_DATA",
          page: tableName,
          data: {
            appliedFilter: currentState.filter.appliedValues,
            pagination: data.pagination,
            search: currentState.search,
          },
        });

        setState((draft) => {
          draft.invoices = data.invoices;
          draft.pagination = data.pagination;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ?? "Failed to fetch full tax invoices";
      errorMessage(message);
    } finally {
      triggerTableLoading(false);
    }
  };

  const getSettings = async () => {
    setState((draft) => {
      draft.settings.isLoading = true;
    });
    try {
      const response = await getFtiDaysSettingApi();
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.settings.formData.request_period_days =
            data?.request_period_days ?? "";
          draft.settings.requestPeriodDays = data?.request_period_days ?? null;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ?? "Failed to fetch FTI day setting",
      );
    } finally {
      setState((draft) => {
        draft.settings.isLoading = false;
      });
    }
  };

  const updateSettings = async () => {
    const requestPeriodDays = Number(state.settings.formData.request_period_days);

    setState((draft) => {
      draft.settings.isSubmitting = true;
    });
    try {
      const response = await updateFtiDaysSettingApi({
        request_period_days: requestPeriodDays,
      });
      const { success, message } = response;
      if (success) {
        setState((draft) => {
          draft.settings.requestPeriodDays = requestPeriodDays;
          draft.settings.hasChanges = false;
        });
        successMessage(message);
      } else {
        errorMessage(message);
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ?? "Failed to update FTI day setting",
      );
    } finally {
      setState((draft) => {
        draft.settings.isSubmitting = false;
      });
    }
  };

  //   ============================================================== UTIL FUNCTIONS ==============================================================
  const triggerTableLoading = (isLoading) => {
    setState((draft) => {
      draft.isLoading = isLoading;
    });
  };

  const getRequestParams = ({ pagination, search, filter }) => {
    const { status, stores, from_date, to_date } = filter.appliedValues;
    const params = { page: pagination.pageIndex, per_page: pagination.pageSize };

    if (search) params.search = search;
    if (status) params.status = status.value;
    if (stores.length > 0) params.store_id = stores.map(({ value }) => value);
    if (from_date) params.start_date = from_date;
    if (to_date) params.end_date = to_date;

    return params;
  };

  // Util function to handle navigation between pages of the table
  const gotoPage = (pageIndex) => {
    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
    });

    getInvoices({
      ...state,
      pagination: { ...state.pagination, pageIndex },
    });
  };

  // Util function to handle table page-size changes
  const setPageSize = (e) => {
    const pageSize = +e.target.value;
    const pageIndex = 1;

    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
      draft.pagination.pageSize = pageSize;
    });

    getInvoices({
      ...state,
      pagination: { ...state.pagination, pageIndex, pageSize },
    });
  };

  const handleSearch = (value) => {
    const pagination = { ...state.pagination, pageIndex: 1 };

    setState((draft) => {
      draft.search = value;
      draft.pagination = pagination;
    });

    getInvoices({ ...state, pagination, search: value });
  };

  const handleFilterChange = ({ name, value }) => {
    setState((draft) => {
      draft.filter.values[name] = value;
      draft.filter.isFilterApplied = false;
    });
  };

  // Applies the given filter values and refetches the list from the first page
  const applyFilterValues = (values) => {
    const pagination = { ...state.pagination, pageIndex: 1 };

    setState((draft) => {
      draft.pagination = pagination;
      draft.filter.values = values;
      draft.filter.appliedValues = values;
      draft.filter.isFilterApplied = true;
    });

    getInvoices({
      ...state,
      pagination,
      filter: { ...state.filter, values, appliedValues: values },
    });
  };

  const handleApplyFilter = () => applyFilterValues(state.filter.values);

  // Function to handle reset the specific filter, each selected store has its own chip
  const handleClearFilter = ({ accessorKey, value }) => {
    const { appliedValues } = state.filter;

    applyFilterValues({
      ...appliedValues,
      [accessorKey]:
        accessorKey === "stores"
          ? appliedValues.stores.filter((store) => store.value !== value)
          : null,
    });
  };

  // Function to handle reset all filter
  const handleClearAllFilter = () => applyFilterValues(defaultFilterValues);

  // The selected order is kept on close so the dialog can still render while it animates out
  const setCreateFlowStep = (step, order) => {
    setState((draft) => {
      draft.createFlow.step = step;
      if (order) draft.createFlow.order = order;
    });
  };

  const handleOpenOrderSelection = () => setCreateFlowStep("orders");

  const handleSelectOrder = (order) => setCreateFlowStep("form", order);

  const handleCloseCreateFlow = () => setCreateFlowStep(null);

  // Submits the FTI form for the order picked in the create flow
  const handleCreateFti = (payload) =>
    createFtiApi(state.createFlow.order.id, payload);

  const handleFtiCreated = (fti) =>
    navigate(
      `${isStoreAdmin ? "/store-admin" : ""}/full-tax-invoice/${fti.id}/details`
    );

  const handleSettingsChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.settings.formData[name] = value;
      draft.settings.hasChanges = true;
    });
  };

  const handleSettingsSubmit = () => {
    if (settingsValidator.current.allValid()) {
      updateSettings();
    } else {
      settingsValidator.current.showMessages();
      setForceUpdate((previous) => previous + 1);
    }
  };

  const appliedFilters = React.useMemo(() => {
    const { status, stores, from_date, to_date } = state.filter.appliedValues;

    return [
      status && { type: "Status", label: status.label, accessorKey: "status" },
      ...stores.map((store) => ({
        type: "Store",
        label: store.label,
        accessorKey: "stores",
        value: store.value,
      })),
      from_date && {
        type: "From Date",
        label: convertDate({ date: from_date, format: "DD MMM YYYY" }),
        accessorKey: "from_date",
      },
      to_date && {
        type: "To Date",
        label: convertDate({ date: to_date, format: "DD MMM YYYY" }),
        accessorKey: "to_date",
      },
    ].filter(Boolean);
  }, [state.filter.appliedValues]);

  //   ================================================================ USE EFFECTS =================================================================
  React.useEffect(() => {
    getInvoices(state);
    getSettings();
  }, []);

  return {
    state,
    isStoreAdmin,
    isCreateVisible,
    isEditable,
    appliedFilters,
    settingsValidator,
    gotoPage,
    setPageSize,
    handleSearch,
    handleFilterChange,
    handleApplyFilter,
    handleClearFilter,
    handleClearAllFilter,
    handleOpenOrderSelection,
    handleSelectOrder,
    handleCloseCreateFlow,
    handleCreateFti,
    handleFtiCreated,
    handleSettingsChange,
    handleSettingsSubmit,
  };
};
